import React from 'react';
import { CheckCircle2Icon, AlertTriangleIcon, MapPinOffIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, Trash2Icon } from 'lucide-react';
import { defaultAccess, useFunnel } from '../../contexts/FunnelContext';
import { isStringKey } from '../../data/i18n';
import type { StepErrors } from '../../utils/validation';
import type { AddressValue, ExtraStop, FunnelData, StopRole } from '../../types/funnel';
import { StepHeading } from './StepHeading';
import { TextField } from './TextField';
import { Segmented } from './Segmented';
import { AddressFields } from './AddressFields';
import { HelpAnswers } from './HelpAnswers';
const copy = {
  en: {
    stopsTitle: 'Extra stops',
    stopsSub: 'Picking up or dropping off somewhere else on the way? Add each stop in route order.',
    add: 'Add an extra stop',
    stop: (n: number) => `Stop ${n}`,
    role: 'At this stop we…',
    pickup: 'Pick up',
    dropoff: 'Drop off',
    both: 'Both',
    items: 'What gets loaded or unloaded here?',
    up: 'Move stop up',
    down: 'Move stop down',
    remove: 'Remove stop',
    route: 'Route order'
  },
  es: {
    stopsTitle: 'Paradas adicionales',
    stopsSub: '¿Recogemos o entregamos en otro lugar en el camino? Agregue cada parada en el orden de la ruta.',
    add: 'Agregar una parada',
    stop: (n: number) => `Parada ${n}`,
    role: 'En esta parada…',
    pickup: 'Recogemos',
    dropoff: 'Entregamos',
    both: 'Ambas',
    items: '¿Qué se carga o descarga aquí?',
    up: 'Subir parada',
    down: 'Bajar parada',
    remove: 'Quitar parada',
    route: 'Orden de la ruta'
  }
};
function originValue(d: FunnelData): AddressValue {
  return {
    street: d.originStreet,
    unit: d.originUnit,
    building: d.originBuilding,
    buildingNo: d.originBuildingNo,
    city: d.originCity,
    state: d.originState,
    zip: d.originZip,
    placeId: d.originPlaceId,
    status: d.originAddrStatus
  };
}
function destValue(d: FunnelData): AddressValue {
  return {
    street: d.destStreet,
    unit: d.destUnit,
    building: d.destBuilding,
    buildingNo: d.destBuildingNo,
    city: d.destCity,
    state: d.destState,
    zip: d.destZip,
    placeId: d.destPlaceId,
    status: d.destAddrStatus
  };
}
function toFlat(prefix: 'origin' | 'dest', p: Partial<AddressValue>): Partial<FunnelData> {
  const out: Record<string, unknown> = {};
  const map: Record<keyof AddressValue, string> = {
    street: 'Street',
    unit: 'Unit',
    building: 'Building',
    buildingNo: 'BuildingNo',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    placeId: 'PlaceId',
    status: 'AddrStatus'
  };
  for (const [k, v] of Object.entries(p)) out[`${prefix}${map[k as keyof AddressValue]}`] = v;
  return out as Partial<FunnelData>;
}
export function StepAddresses({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    lang,
    data,
    update,
    quote
  } = useFunnel();
  const c = copy[lang];
  const err = (k: string) => errors[k] ? t(errors[k]!) : undefined;
  const zoneText = isStringKey(quote.zoneReasonKey) ? t(quote.zoneReasonKey) : '';
  const zoneMsg = quote.zone === 'eligible_prelim' ? {
    cls: 'border-lime-dark/30 bg-lime-soft',
    icon: <CheckCircle2Icon className="h-5 w-5 text-lime-dark" data-id="element-632" data-mp="n5le8cn5inao" />,
    text: zoneText
  } : quote.zone === 'review' ? {
    cls: 'border-ink-300 bg-ink-100',
    icon: <AlertTriangleIcon className="h-5 w-5 text-ink-600" data-id="element-633" data-mp="wveq7er7h4fg" />,
    text: zoneText
  } : quote.zone === 'outside' ? {
    cls: 'border-magenta/30 bg-magenta-soft',
    icon: <MapPinOffIcon className="h-5 w-5 text-magenta-dark" data-id="element-634" data-mp="16tb0y5g8dxd1" />,
    text: zoneText
  } : null;
  const setStops = (stops: ExtraStop[]) => update({
    stops
  });
  const patchStop = (id: string, p: Partial<ExtraStop>) => setStops(data.stops.map(s => s.id === id ? {
    ...s,
    ...p
  } : s));
  const move = (i: number, dir: -1 | 1) => {
    const next = [...data.stops];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setStops(next);
  };
  const addStop = () => setStops([...data.stops, {
    id: `st${Date.now()}`,
    street: '',
    unit: '',
    building: '',
    buildingNo: '',
    city: '',
    state: '',
    zip: '',
    placeId: null,
    status: 'typed',
    role: 'pickup',
    items: '',
    access: {
      ...defaultAccess
    }
  }]);
  return <div data-id="element-635" data-mp="3j4m6px8d9la7">
      <StepHeading title={t('addrTitle')} intro={t('addrIntro')} data-id="element-636" data-mp="3o1mgai4fd662" />
      <div className="grid gap-10 md:grid-cols-2" data-id="element-637" data-mp="3o25q1bjx8o90">
        <fieldset data-id="element-638" data-mp="3t18t4qgcckec">
          <legend className="mb-3 font-display text-lg font-extrabold text-ink" data-id="element-639" data-mp="3sznxsym7cxff">{t('fromLabel')}</legend>
          <AddressFields value={originValue(data)} onChange={p => update(toFlat('origin', p))} lang={lang} zipError={err('originZip')} data-id="element-640" data-mp="2ve04iml1mv84" />
        </fieldset>
        <fieldset data-id="element-641" data-mp="35b4r2niswspg">
          <legend className="mb-3 font-display text-lg font-extrabold text-ink" data-id="element-642" data-mp="35amkoy2fcy3t">{t('toLabel')}</legend>
          <AddressFields value={destValue(data)} onChange={p => update(toFlat('dest', p))} lang={lang} zipError={err('destZip')} data-id="element-643" data-mp="3a24mtpg6p4qd" />
        </fieldset>
      </div>

      {zoneMsg && <div role="status" className={`mt-6 flex items-start gap-3 rounded-xl border p-4 text-[15px] leading-relaxed text-ink ${zoneMsg.cls}`} data-id="element-644" data-mp="2hlafsae4rc7b">
          <span className="mt-0.5 shrink-0" data-id="element-645" data-mp="2a1j00rp8ziwm">{zoneMsg.icon}</span>
          {zoneMsg.text}
        </div>}

      <section className="mt-10 border-t border-ink-200 pt-8" data-id="element-646" data-mp="253pt1wg4ekva">
        <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-647" data-mp="202ts9rr2feu3">{c.stopsTitle}</h2>
        <p className="mt-1 text-sm text-ink-500" data-id="element-648" data-mp="1v5a685o4lxrl">{c.stopsSub}</p>

        {data.stops.length > 0 && <ol aria-label={c.route} className="mt-5 space-y-5" data-id="element-649" data-mp="1r3f1er6zl292">
            {data.stops.map((s, i) => <li key={s.id} className="rounded-2xl bg-white p-4 ring-1 ring-ink-200 sm:p-5" data-id="element-650" data-mp="1mbf99kx5rdn1" data-mpi={i}>
                <div className="mb-4 flex items-center gap-2" data-id="element-651" data-mp="1hckinwsy8ksd">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-sm font-bold text-lime" data-id="element-652" data-mp="1ceakrefub632">{i + 1}</span>
                  <h3 className="flex-1 font-display text-lg font-extrabold text-ink" data-id="element-653" data-mp="17e4mvgiz8ncj">{c.stop(i + 1)}</h3>
                  <button type="button" aria-label={c.up} disabled={i === 0} onClick={() => move(i, -1)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-ink-200 hover:bg-ink-100 disabled:opacity-30" data-id="element-654" data-mp="13enkivyuk6bl">
                    <ArrowUpIcon className="h-4 w-4" data-id="element-655" data-mp="xph7m2xw0fj7" />
                  </button>
                  <button type="button" aria-label={c.down} disabled={i === data.stops.length - 1} onClick={() => move(i, 1)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-ink-200 hover:bg-ink-100 disabled:opacity-30" data-id="element-656" data-mp="jtdjmrdlx3f6">
                    <ArrowDownIcon className="h-4 w-4" data-id="element-657" data-mp="1ghthrbus8l3w" />
                  </button>
                  <button type="button" aria-label={c.remove} onClick={() => setStops(data.stops.filter(x => x.id !== s.id))} className="flex h-10 w-10 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink" data-id="element-658" data-mp="12qohc7jlbosi">
                    <Trash2Icon className="h-4 w-4" data-id="element-659" data-mp="1veatw5asq3ri" />
                  </button>
                </div>
                <AddressFields value={s} onChange={p => patchStop(s.id, p)} lang={lang} zipError={err(`stop_${s.id}`)} data-id="element-660" data-mp="2073xuqyfx82j" />
                <div className="mt-4 grid gap-4 md:grid-cols-[auto_1fr]" data-id="element-661" data-mp="29j1ey948u794">
                  <Segmented<StopRole> label={c.role} value={s.role} onChange={v => patchStop(s.id, {
              role: v
            })} options={[{
              value: 'pickup',
              label: c.pickup
            }, {
              value: 'dropoff',
              label: c.dropoff
            }, {
              value: 'both',
              label: c.both
            }]} data-id="element-662" data-mp="25i7tdme9x4je" />
                  <TextField label={c.items} value={s.items} onChange={v => patchStop(s.id, {
              items: v.slice(0, 200)
            })} data-id="element-663" data-mp="1mm7tzqiyuyps" />
                </div>
              </li>)}
          </ol>}

        <button type="button" onClick={addStop} className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-dashed border-ink-300 bg-white px-4 font-semibold text-ink hover:border-ink" data-id="element-664" data-mp="1vjs1vibc3t9d">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-665" data-mp="372matg1x7e1o" />
          {c.add}
        </button>
      </section>

      <HelpAnswers screen="stepAddresses" className="mt-10" data-id="element-666" data-mp="19cnwnztixa30" />
    </div>;
}